import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import NoPhotographyIcon from '@mui/icons-material/NoPhotography';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import {Badge, ListItemIcon, ListItemText, MenuItem} from '@mui/material';
import {useCallback, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {useAuthUser} from '../auth/hooks/useAuthUser';
import {useTranslation} from '../i18n/hooks/useTranslation';
import {Translation} from '../i18n/Translation';
import {DropDownMenu} from '../navigation/DropDownMenu';
import {ComponentWithRequiredChildren, ComponentWithoutChildren, PropsWithRequiredChildren} from '../react/types';
import {EditAvatarDialog} from './EditAvatarDialog';
import {useCurrentUser} from './hooks/useCurrentUser';

export const EditAvatarBadge: ComponentWithRequiredChildren = ({children}: PropsWithRequiredChildren) => {
  const [editAvatarDialogOpen, setEditAvatarDialogOpen] = useState<boolean>(false);
  const openEditAvatarDialog = useCallback(() => setEditAvatarDialogOpen(true), [setEditAvatarDialogOpen]);
  const closeEditAvatarDialog = useCallback(() => setEditAvatarDialogOpen(false), [setEditAvatarDialogOpen]);

  const currentUser = useCurrentUser();
  const {setValue} = useFormContext();
  const {translate} = useTranslation('user');

  const changeImage = useCallback(
    (url: string) => {
      setValue('avatar', {source: 'user', url}, {shouldDirty: true});
      closeEditAvatarDialog();
      return Promise.resolve();
    },
    [closeEditAvatarDialog, setValue],
  );

  const editAvatarLabel = translate('user:avatar.edit.label');
  return (
    <>
      <Badge
        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
        badgeContent={
          <DropDownMenu
            label={editAvatarLabel}
            menuDirection="right"
            menuIcon={CameraAltIcon}
            menuIconProps={{fontSize: 'small'}}
            tooltip={editAvatarLabel}
            variant="icon"
          >
            <MenuItem onClick={openEditAvatarDialog}>
              <ListItemIcon>
                <AddAPhotoIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <Translation keys="user:avatar.edit.menu.new">
                {(text) => <ListItemText primary={text} slotProps={{primary: {color: 'primary'}}} />}
              </Translation>
            </MenuItem>
            <RestoreDefaultImageMenuItem />
            <RemoveImageMenuItem />
          </DropDownMenu>
        }
        overlap="circular"
      >
        {children}
      </Badge>
      <EditAvatarDialog
        handleCancel={closeEditAvatarDialog}
        handleSelect={changeImage}
        open={editAvatarDialogOpen}
        userId={currentUser.get().id}
      />
    </>
  );
};

const RestoreDefaultImageMenuItem: ComponentWithoutChildren = () => {
  const {photoURL} = useAuthUser().data;
  const {setValue} = useFormContext();

  const restoreDefaultImage = useCallback(() => {
    if (photoURL) {
      setValue('avatar', {source: 'auth', url: photoURL}, {shouldDirty: true});
    } else {
      setValue('avatar', undefined, {shouldDirty: true});
    }
  }, [photoURL, setValue]);

  return (
    <MenuItem onClick={restoreDefaultImage}>
      <ListItemIcon>
        <SettingsBackupRestoreIcon fontSize="small" />
      </ListItemIcon>
      <Translation keys="user:avatar.edit.menu.restore">{(text) => <ListItemText primary={text} />}</Translation>
    </MenuItem>
  );
};

const RemoveImageMenuItem: ComponentWithoutChildren = () => {
  const {setValue} = useFormContext();

  const removeImage = useCallback(() => {
    setValue('avatar', undefined, {shouldDirty: true});
  }, [setValue]);

  return (
    <MenuItem onClick={removeImage}>
      <ListItemIcon>
        <NoPhotographyIcon fontSize="small" />
      </ListItemIcon>
      <Translation keys="user:avatar.edit.menu.remove">{(text) => <ListItemText primary={text} />}</Translation>
    </MenuItem>
  );
};
